import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { patchApplicationService } from '../../api/application';
import { Debt } from '../../api/getDebts';
import { RootState } from '../store';
import { IFieldState, IFormData, IFormState } from './applicationFormTypes';

type PatchRequest = {
    applicationReferenceNumber: string;
    formFieldStates: IFieldState[];
};

export const patchForm = createAsyncThunk<IFormData, PatchRequest, { state: RootState }>(
    'form/patchForm',
    async (request: PatchRequest, { getState, dispatch: _dispatch }) => {
        const state = getState();
        return await patchApplicationService({
            applicationReferenceNumber: request.applicationReferenceNumber,
            authenticationData: state.authentication.authenticationData,
            formFieldStates: request.formFieldStates,
            countryCode: state.settings.countryCode,
            apiKey: state.settings.apiKey,
            languageCode: state.settings.languageCode,
            proxyBaseUrl: state.settings.proxyBaseUrl,
        });
    },
);

const addUpdateFormFieldArray = (existingFields: IFieldState[], field: IFieldState): IFieldState[] => {
    if (field !== null && !existingFields.find((existingField) => existingField.fieldName === field.fieldName)) {
        return [...existingFields, field];
    }
    return existingFields.map((existingField) => {
        if (existingField.fieldName === field.fieldName) {
            const value = { value: field.value };
            const checked = getFieldChecked(existingField, field);

            return { ...existingField, ...field, ...value, ...checked };
        } else {
            return existingField;
        }
    });
};

const getFieldChecked = (existingField: IFieldState, field: IFieldState) => {
    // Get if the local field state has checked
    const existingFieldChecked = existingField.checked !== undefined ? { checked: existingField.checked } : {};

    // Check if the server field state is checked and override the local settings
    const checked = field.checked !== undefined ? { checked: field.checked } : existingFieldChecked;
    return checked;
};

export const addUpdateMultiValueFormFieldArray = (
    existingFields: IFieldState[],
    field: IFieldState,
    isRequired: boolean,
    errorMessageRequired: string,
    pageNumber: number,
) => {
    if (field === null) {
        return existingFields;
    }
    field.editReference = {
        pageNumber: pageNumber,
    };
    if (field.checked) {
        // Clear errros
        const remainingFields = clearErrorForMultiValueFormFieldArray(existingFields, field);
        // Do not add value if it already exists
        if (!valueExists(remainingFields, field)) {
            return [...remainingFields, field];
        } else {
            return remainingFields;
        }
    }

    // Remove the field if it wasn't checked
    const remainingFields = removeField(existingFields, field);
    if (isRequired && !fieldExists(remainingFields, field)) {
        return [
            ...remainingFields,
            {
                fieldName: field.fieldName,
                hasError: true,
                errorMessage: errorMessageRequired,
            } as IFieldState,
        ];
    }
    return remainingFields;
};

const valueExists = (existingFields: IFieldState[], field: IFieldState): boolean =>
    !!existingFields.find(
        (existingField) => existingField.fieldName === field.fieldName && existingField.value === field.value,
    );

const removeField = (existingFields: IFieldState[], field: IFieldState): IFieldState[] =>
    existingFields.filter(
        (existingField) => !(existingField.fieldName === field.fieldName && existingField.value === field.value),
    );

const clearErrorForMultiValueFormFieldArray = (existingFields: IFieldState[], field: IFieldState): IFieldState[] =>
    existingFields.filter((existingField) => !(existingField.fieldName === field.fieldName && existingField.hasError));

const fieldExists = (fields: IFieldState[], field: IFieldState): boolean =>
    !!fields.find((f) => f.fieldName === field.fieldName);

export const initialFormState = {
    isLoading: false,
    formFieldStates: [],
    consolidations: [],
    internalLoans: [],
} as IFormState;

const formSlice = createSlice({
    name: 'form',
    initialState: initialFormState,
    reducers: {
        invalidateForm: () => initialFormState,
        addOrUpdateFormField: (state: IFormState, action: PayloadAction<IFieldState>) => {
            state.formFieldStates = addUpdateFormFieldArray(state.formFieldStates, action.payload);
        },
        addOrUpdateFormFields: (state: IFormState, action: PayloadAction<IFieldState[]>) => {
            (action.payload as IFieldState[]).forEach(
                (fieldState) => (state.formFieldStates = addUpdateFormFieldArray(state.formFieldStates, fieldState)),
            );
        },
        setConsolidations: (state: IFormState, action: PayloadAction<Debt[]>) => {
            state.consolidations = action.payload;
        },
        setInternalLoans: (state: IFormState, action: PayloadAction<Debt[]>) => {
            state.internalLoans = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(patchForm.fulfilled, (state) => {
            state.isLoading = false;
        });
        builder.addCase(patchForm.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(patchForm.rejected, (state) => {
            state.isLoading = false;
        });
    },
});

export const { addOrUpdateFormField, addOrUpdateFormFields, setConsolidations, setInternalLoans, invalidateForm } =
    formSlice.actions;

export default formSlice.reducer;
